import React from 'react';
import {useNavigate} from 'react-router-dom';
import {ConfirmationModal, ConfirmationSuccessIcon} from '@rocket-ui/components';
import {useCreatePdfReference} from '@rocket-ui/lk-api';
import {ROUTES} from 'app/router/routes';
import {
	CERTIFICATE_SUCCESS_DESCRIPTION,
	CERTIFICATE_SUCCESS_TITLE,
	CertificateEmailStep,
	ProfileAlertsBanner,
	CertificateTypeStep,
	CertificateYearStep,
	DEFAULT_CERTIFICATE_TYPE,
	StepProgress,
	getDefaultCertificateYear,
	isProfileEmailValid,
	useProfile,
	type CertificateType,
} from 'features/profile';
import {RegistrationButton} from 'features/registration';
import styles from './AddAccountPaymentPage.module.css';

type CertificateStep = 'type' | 'year' | 'email';

const STEPS: CertificateStep[] = ['type', 'year', 'email'];

const backIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M10 6L4 12L10 18" stroke="currentColor" strokeWidth="1.6" />
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M20 12.9135L5 12.9135L5 11.1992L20 11.1992L20 12.9135Z"
			fill="currentColor"
		/>
	</svg>
);

const closeIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M3.99805 3.99805L20.0004 20.0004" stroke="currentColor" strokeWidth="1.6" />
		<path d="M3.99805 20L20.0004 3.99768" stroke="currentColor" strokeWidth="1.6" />
	</svg>
);

function getInitialEmail(profileEmail: string): string {
	return profileEmail && profileEmail !== '—' ? profileEmail : '';
}

export function GenerateCertificatePage(): React.ReactElement {
	const navigate = useNavigate();
	const {profile} = useProfile();
	const createPdfReferenceMutation = useCreatePdfReference();
	const [step, setStep] = React.useState<CertificateStep>('type');
	const [certificateType, setCertificateType] = React.useState<CertificateType>(DEFAULT_CERTIFICATE_TYPE);
	const [year, setYear] = React.useState(getDefaultCertificateYear);
	const [email, setEmail] = React.useState(() => getInitialEmail(profile.email));
	const [isExitConfirmOpen, setIsExitConfirmOpen] = React.useState(false);
	const [isSuccessOpen, setIsSuccessOpen] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string | null>(null);

	React.useEffect(() => {
		const nextEmail = getInitialEmail(profile.email);

		if (!nextEmail) {
			return;
		}

		setEmail((prev) => prev || nextEmail);
	}, [profile.email]);

	const stepIndex = STEPS.indexOf(step);
	const showBack = step !== 'type';
	const isEmailValid = isProfileEmailValid(email);
	const isSubmitting = createPdfReferenceMutation.isLoading;
	const showFooterButton = !isSuccessOpen && (step !== 'email' || isEmailValid || isSubmitting);

	const handleConfirmExit = () => {
		setIsExitConfirmOpen(false);
		navigate(ROUTES.profile);
	};

	const handleSuccessContinue = () => {
		setIsSuccessOpen(false);
		navigate(ROUTES.profile);
	};

	const handleBack = () => {
		if (isSuccessOpen || isSubmitting) {
			return;
		}

		if (step === 'email') {
			setStep('year');
			setSubmitError(null);
			return;
		}

		if (step === 'year') {
			setStep('type');
		}
	};

	const handleSubmitCertificate = () => {
		if (!isEmailValid || isSubmitting) {
			return;
		}

		setSubmitError(null);
		createPdfReferenceMutation.mutate(
			{
				requestYear: year,
				email: email.trim(),
				referenceType: certificateType,
			},
			{
				onSuccess: () => {
					setIsSuccessOpen(true);
				},
				onError: () => {
					setSubmitError('Не получилось сформировать справку. Попробуйте ещё раз');
				},
			},
		);
	};

	const handleContinue = () => {
		if (isSubmitting) {
			return;
		}

		if (step === 'type') {
			setStep('year');
			return;
		}

		if (step === 'year') {
			setStep('email');
			setSubmitError(null);
			return;
		}

		if (step === 'email') {
			handleSubmitCertificate();
		}
	};

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<div className={styles.headerInner}>
					{showBack ? (
						<button type="button" className={styles.backButton} onClick={handleBack} aria-label="Назад">
							{backIcon}
						</button>
					) : (
						<span className={styles.headerSpacer} aria-hidden />
					)}
					<StepProgress currentStep={stepIndex} totalSteps={STEPS.length} className={styles.progress} />
					<button
						type="button"
						className={styles.closeButton}
						onClick={() => {
							if (isSuccessOpen || isSubmitting) {
								return;
							}

							setIsExitConfirmOpen(true);
						}}
						aria-label="Закрыть"
					>
						{closeIcon}
					</button>
				</div>
			</header>

			<div className={styles.content}>
				<div className={styles.contentInner}>
					<ProfileAlertsBanner />
					{step === 'type' ? (
						<CertificateTypeStep value={certificateType} onChange={setCertificateType} />
					) : null}

					{step === 'year' ? <CertificateYearStep value={year} onChange={setYear} /> : null}

					{step === 'email' ? (
						<>
							<CertificateEmailStep
								value={email}
								onChange={(nextEmail) => {
									setSubmitError(null);
									setEmail(nextEmail);
								}}
							/>
							{submitError ? <p className={styles.errorText}>{submitError}</p> : null}
						</>
					) : null}
				</div>
			</div>

			{showFooterButton ? (
				<footer className={styles.footer}>
					<div className={styles.footerInner}>
						<RegistrationButton
							variant="primary"
							className={styles.continueButton}
							loading={isSubmitting}
							disabled={isSubmitting}
							onClick={handleContinue}
						>
							{step === 'email' ? 'Сформировать' : 'Продолжить'}
						</RegistrationButton>
					</div>
				</footer>
			) : null}

			<ConfirmationModal
				isOpen={isExitConfirmOpen}
				title="Хотите выйти?"
				description="Вы уверены, что хотите прервать формирование справки?"
				confirmText="Выйти"
				onConfirm={handleConfirmExit}
				onCancel={() => setIsExitConfirmOpen(false)}
			/>

			<ConfirmationModal
				isOpen={isSuccessOpen}
				title={CERTIFICATE_SUCCESS_TITLE}
				description={CERTIFICATE_SUCCESS_DESCRIPTION}
				confirmText="Продолжить"
				illustration={ConfirmationSuccessIcon}
				onConfirm={handleSuccessContinue}
				onCancel={handleSuccessContinue}
			/>
		</div>
	);
}
